import type { RefCallback } from 'react'
import React, {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
} from 'react'

import { useControllableState } from '../hooks/useControllableState'
import type { PopoverProps } from '../Popover/Popover'
import { Popover } from '../Popover/Popover'
import { PopoverStoreContext } from '../Popover/PopoverBoundary'
import type { PopoverState } from '../Popover/popoverTypes'
import { mergeRefs } from '../utils/mergeRefs'
import { useOnNestedClickOutside } from '../utils/useOnNestedClickOutside'

export type PopoverOnClickRendererArgs = {
  /**
   * Toggle the popover open/closed depending on the current state.
   */
  toggle: () => void
  /**
   * Close the popover rendering.
   */
  close: () => void
  /**
   * Current open state.
   */
  isOpen: boolean
}

export type PopoverOnClickRef = {
  open: () => void
  close: () => void
  isOpen: boolean
}

export type PopoverOnClickProps = {
  /**
   * The current value for this property.
   * Fallsback to `defaultOpen` if not provided.
   */
  open?: boolean
  /**
   * The default value for the `open` property.
   */
  defaultOpen?: boolean
  /**
   * Callback called when the `open` property value changes.
   */
  onIsOpenChange?: (isOpen: boolean) => void

  /**
   * Component that will be rendered regardless of the popover state.
   * It is responsible for toggling the popover on/off through the args it is given.
   *
   * Example:
   * ```ts
   * target={({ toggle }) => <button onClick={toggle}>toggle 💃</button>}
   * ```
   */
  target: (
    args: PopoverOnClickRendererArgs & { ref: RefCallback<HTMLOrSVGElement> }
  ) => React.ReactNode

  /**
   * Component that will be rendered upon toggling the popover on.
   */
  content: (
    args: PopoverOnClickRendererArgs &
      PopoverState & { ref: RefCallback<HTMLOrSVGElement> }
  ) => React.ReactNode
  closeOnClickOutside?: boolean
  closeOnEscape?: boolean
  zIndex?: string
  containerStyle?: Partial<CSSStyleDeclaration>
  shouldRefocus?: (element: HTMLElement) => boolean
  ref?: React.Ref<PopoverOnClickRef>
} & Omit<
  PopoverProps,
  'isOpen' | 'children' | 'content' | 'containerStyle' | 'target' | 'ref'
>

function isHtmlElement(element: unknown): element is HTMLElement {
  return element instanceof HTMLElement
}

export function PopoverOnClick({
  open,
  defaultOpen,
  onIsOpenChange,
  disabled,
  target,
  content,
  closeOnClickOutside = true,
  closeOnEscape = true,
  shouldRefocus = () => true,
  padding = 8,
  positions = ['bottom'],
  align = 'start',
  zIndex = '5',
  containerStyle = {},
  reposition = true,
  boundaryInset = 8,
  onClickOutside,
  ref: controlRef,
  ...props
}: PopoverOnClickProps) {
  const [isOpen = false, setIsOpen] = useControllableState<boolean>({
    defaultProp: defaultOpen,
    prop: open,
    onChange: onIsOpenChange,
  })
  const previouslyActiveElement = React.useRef<HTMLElement | null>(null)

  const toggle = useCallback(() => {
    setIsOpen((isOpen) => {
      const newIsOpen = !isOpen

      if (newIsOpen) {
        if (isHtmlElement(document.activeElement)) {
          previouslyActiveElement.current = document.activeElement
        }
      } else {
        previouslyActiveElement.current?.focus()
        previouslyActiveElement.current = null
      }

      return newIsOpen
    })
  }, [setIsOpen])

  const handleOpen = useCallback(() => {
    if (isHtmlElement(document.activeElement)) {
      previouslyActiveElement.current = document.activeElement
    }
    setIsOpen(true)
  }, [setIsOpen])

  const handleClose = useCallback(() => {
    const { current: previousElement } = previouslyActiveElement
    if (previousElement && shouldRefocus(previousElement)) {
      previousElement.focus()
    }
    previouslyActiveElement.current = null
    setIsOpen(false)
  }, [setIsOpen, shouldRefocus])

  useImperativeHandle(
    controlRef,
    () => ({
      open: handleOpen,
      close: handleClose,
      isOpen,
    }),
    [handleOpen, handleClose, isOpen]
  )

  const {
    ref: contentRef,
    targetRef,
    ChildRegistrationContext,
  } = useOnNestedClickOutside({
    onClickOutside(event) {
      if (onClickOutside) {
        onClickOutside(event)
      }
      if (closeOnClickOutside) {
        handleClose()
      }
    },
    disabled: !isOpen,
  })

  const store = useContext(PopoverStoreContext)
  useEffect(() => {
    const item = { close: handleClose, closeOnEscape }
    if (isOpen) {
      store.register(item)
      return function onLeave() {
        store.unregister(item)
      }
    }
    return function onLeave() {}
  }, [closeOnEscape, handleClose, isOpen, store])

  return (
    <ChildRegistrationContext>
      <Popover
        {...props}
        padding={padding}
        positions={positions}
        align={align}
        isOpen={isOpen}
        content={(args) =>
          content({
            ...args,
            ref: contentRef,
            toggle,
            isOpen,
            close: handleClose,
          })
        }
        ref={targetRef}
        reposition={reposition}
        boundaryInset={boundaryInset}
        containerStyle={{
          ...containerStyle,
          zIndex,
        }}
        target={({ ref }) =>
          target({
            ref: mergeRefs(ref, targetRef),
            toggle,
            isOpen,
            close: handleClose,
          })
        }
      />
    </ChildRegistrationContext>
  )
}
